<script setup lang="ts">
import type { ISpecialProjectFeedItemDataClientDTO } from '~/shared/api/client/special-project-feed/special-project-feed.dto';
import { resizeImage } from '~/shared/lib/resizeImage';

const props = defineProps<ISpecialProjectFeedItemDataClientDTO>();

const setContentTab = inject<(tab: 'main' | 'news' | 'companies') => void>(
  'setContentTab',
  () => undefined,
);

const isRedirect = computed(() =>
  Boolean(props.news?.redirect && props.news.redirect.length > 0),
);

const link = computed(() => {
  if (!props.news) {
    return '';
  }

  return isRedirect.value && props.news.redirect
    ? props.news.redirect
    : `/${props.news.type}/${props.news.id}`;
});

const categoryLink = computed(() =>
  props.news?.category?.id ? `/category/${props.news.category.id}` : '',
);

const handleItemClick = (event: MouseEvent) => {
  const target = event.target;

  if (
    isRedirect.value ||
    !(target instanceof Element) ||
    !target.closest('a')
  ) {
    return;
  }

  setContentTab('main');
};
</script>

<template>
  <div v-if="props.news" :class="$style.item" @click.capture="handleItemClick">
    <NuxtLink
      v-if="props.news.category?.name && categoryLink"
      :class="[
        $style.item__category,
        {
          [$style.item__category_paddingCategory!]:
            props.news.category?.image_category,
        },
      ]"
      :to="categoryLink"
    >
      {{ props.news.category.name }}
    </NuxtLink>

    <NuxtLink
      :class="$style.item__title"
      :to="link"
      :target="isRedirect ? '_blank' : undefined"
    >
      {{ props.title || props.news.title }}
    </NuxtLink>

    <NuxtLink
      v-if="props.news.category?.image_category"
      :class="$style.item__categoryImageWrapper"
      :to="categoryLink"
      :target="isRedirect ? '_blank' : undefined"
    >
      <img
        :class="$style.item__categoryImage"
        :src="
          resizeImage(props.news.category.image_category.file_url, {
            width: 160,
          })
        "
        :alt="props.news.category.name"
      />
    </NuxtLink>
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-flow: column nowrap;
  gap: var(--space-1x);
  padding: var(--space-2-5x);
  position: relative;
  border-top: 1px solid var(--special-feed-divider-color, transparent);

  &:first-of-type {
    border-top: none;
  }

  &__category {
    display: block;
    @include text_caption2_caps;
    color: inherit;
    opacity: 0.7;
    text-decoration: none;

    &_paddingCategory {
      padding-right: 64px;
      z-index: 1;
    }
  }

  &__title {
    display: block;
    @include text_body2;
    color: inherit;
    text-decoration: none;
  }

  &__categoryImage {
    height: 15px;
    width: auto;

    &Wrapper {
      position: absolute;
      display: flex;
      top: 19px;
      right: 20px;
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}
</style>
